import { DEFAULT_COVER_IMAGE } from 'constants.js.erb';
import Link from 'components/Link';
import Image from 'components/Image';
import cn from 'classnames';
import styles from './styles/NewsFeed.module.scss';
import DateString from 'components/DateString';
import { Fragment } from 'react';
import MinuteMediaAd from '../ads/MinuteMediaAd';

function FeedItem({
  path,
  imageUrl,
  imageAlt,
  label,
  title,
  description,
  authorImage,
  authorName,
  date,
  featured
}: {
  path: string;
  imageUrl: string;
  imageAlt: string;
  label: string;
  title?: string;
  description?: string;
  authorImage: string;
  authorName: string;
  date: string;
  featured?: boolean;
}) {
  return (
    <Link className={cn(styles.Item, { [styles.Featured]: featured, [styles.FeedItem]: !featured })} href={path}>
      <div className={styles.ImageColumn}>
        <div className={styles.ImageConatiner}>
          <Image className={styles.Image} src={imageUrl} alt={imageAlt} />
        </div>
      </div>
      <div className={styles.Label}>{label}</div>
      <div className={styles.Title}>{title}</div>
      {featured && description && <div className={styles.Description}>{description}</div>}
      <div className={styles.Author}>
        <Image src={authorImage} className={styles.AuthorImage} />
        {authorName} &bull; <DateString date={date} />
      </div>
    </Link>
  );
}

export default function NewsFeed({
  articles,
  defaultAuthorImage,
  defaultImage,
  games,
  photoGalleries,
  showFeature
}: {
  articles?: {
    id: string;
    authorImage?: string;
    authorName: string;
    date: string;
    description?: string;
    genderSport?: {
      name: string;
    };
    imageUrl?: string;
    imageAlt?: string;
    path: string;
    title: string;
  }[];
  games?: {
    id: string;
    date: string;
    headline?: string;
    webPath: string;
  }[];
  photoGalleries?: {
    id: string;
    coverPhoto: {
      s3Watermark: string | undefined;
      watermark: string;
    };
    webPath: string;
    name: string;
    teams: {
      fullName: string;
    }[];
    photographer: {
      firstName: string;
      lastName: string;
    };
    date: string;
    thumbnailUrl: string | undefined;
  }[];
  defaultAuthorImage?: string;
  defaultImage?: string;
  showFeature?: boolean;
}) {
  const items = [
    ...(articles || []).map(article => ({
      id: `article:${article.id}`,
      path: article.path,
      imageUrl: article.imageUrl || defaultImage || DEFAULT_COVER_IMAGE,
      imageAlt: article.imageAlt || article.title,
      label: article.genderSport?.name || 'Feature',
      title: article.title,
      description: article.description,
      authorImage: article.authorImage || defaultAuthorImage || DEFAULT_COVER_IMAGE /* TODO different default here */,
      authorName: article.authorName,
      date: article.date
    })),
    ...(games || []).map(game => ({
      id: `game:${game.id}`,
      path: game.webPath,
      imageUrl: defaultImage || DEFAULT_COVER_IMAGE,
      imageAlt: game.headline || 'Recap',
      label: 'Recap',
      title: game.headline,
      description: undefined,
      authorImage: defaultAuthorImage || DEFAULT_COVER_IMAGE /* TODO different default here */,
      authorName: 'SBLive',
      date: game.date
    })),
    ...(photoGalleries || []).map(gallery => ({
      id: `gallery:${gallery.id}`,
      path: gallery.webPath,
      imageUrl: gallery.thumbnailUrl || gallery.coverPhoto.s3Watermark || gallery.coverPhoto.watermark || DEFAULT_COVER_IMAGE,
      imageAlt: gallery.name || 'Photo Gallery',
      label: 'Photo Gallery',
      title: `Photo Gallery Posted for ${gallery.teams.map(team => team.fullName).join(' vs. ')}`,
      description: undefined,
      authorImage: defaultAuthorImage || DEFAULT_COVER_IMAGE /* TODO different default here */,
      authorName: `${gallery.photographer.firstName} ${gallery.photographer.lastName}`,
      date: gallery.date
    }))
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <div className={styles.Feed}>
      {items.map(({ id, ...item }, i) => (
        <Fragment key={id}>
          <FeedItem featured={i === 0 && showFeature} {...item} />
          {i === 4 && (
            <div style={{ width: '100%' }}>
              <MinuteMediaAd placement="IN_FEED" />
            </div>
          )}
          {i === 8 && (
            <div style={{ width: '100%' }}>
              <MinuteMediaAd placement="IN_FEED_2" />
            </div>
          )}
        </Fragment>
      ))}
    </div>
  );
}
